import { useEffect, useMemo, useState, Fragment } from "react";
import { BarChart3, Download, Filter, TrendingUp, Users, Award, Inbox, ChevronDown, ChevronRight, FileText, Layers, FolderKanban } from "lucide-react";
import api from "@/lib/api";
import SearchableSelect from "@/components/SearchableSelect";
import { useAuth } from "@/context/AuthContext";
import { formatINR } from "@/utils/formatINR";

const STATUS_OPTIONS = [
  { v: "",          label: "All" },
  { v: "received",  label: "Received" },
  { v: "costing",   label: "Costing" },
  { v: "quote_sent",label: "Quote Sent" },
  { v: "won",       label: "Won" },
  { v: "lost",      label: "Lost" },
  { v: "dropped",   label: "Dropped" },
];

function short(v) {
  const n = Number(v) || 0;
  if (n >= 1e7) return `${(n / 1e7).toFixed(2)} Cr`;
  if (n >= 1e5) return `${(n / 1e5).toFixed(2)} L`;
  return formatINR(n, { decimals: 0 });
}

// Group a list of {month, quarter, ...} rows by quarter, summing numeric fields.
function groupByQuarter(rows, sumFields) {
  const out = {};
  for (const r of rows) {
    const q = r.quarter;
    if (!out[q]) {
      out[q] = { quarter: q, _children: [] };
      for (const f of sumFields) out[q][f] = 0;
    }
    for (const f of sumFields) out[q][f] += Number(r[f] || 0);
    out[q]._children.push(r);
  }
  return Object.values(out).sort((a, b) => a.quarter.localeCompare(b.quarter));
}

function QuarterTable({ rows, columns, monthFmt }) {
  const [open, setOpen] = useState(new Set());
  function toggle(q) {
    setOpen(prev => {
      const next = new Set(prev);
      if (next.has(q)) next.delete(q); else next.add(q);
      return next;
    });
  }
  if (!rows.length) {
    return <div className="text-sm text-slate-400 italic p-4">No data in selected range.</div>;
  }
  return (
    <div className="overflow-x-auto border border-slate-200 rounded-lg">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-slate-600">
          <tr>
            <th className="text-left px-3 py-2 font-medium w-10"></th>
            <th className="text-left px-3 py-2 font-medium">Period</th>
            {columns.map(c => (
              <th key={c.key} className={`px-3 py-2 font-medium ${c.align || "text-right"}`}>{c.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map(qr => {
            const isOpen = open.has(qr.quarter);
            return (
              <Fragment key={qr.quarter}>
                <tr className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => toggle(qr.quarter)} data-testid={`reports-quarter-${qr.quarter}`}>
                  <td className="px-3 py-2 text-slate-400">{isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}</td>
                  <td className="px-3 py-2 font-semibold text-slate-800">{qr.quarter}</td>
                  {columns.map(c => (
                    <td key={c.key} className={`px-3 py-2 font-medium ${c.align || "text-right"}`}>
                      {c.render ? c.render(qr[c.key], qr) : qr[c.key]}
                    </td>
                  ))}
                </tr>
                {isOpen && qr._children.map(mr => (
                  <tr key={qr.quarter + mr.month} className="border-t border-slate-100 bg-slate-50/40 text-slate-600">
                    <td className="px-3 py-1.5"></td>
                    <td className="px-3 py-1.5 pl-8 text-xs">{monthFmt ? monthFmt(mr.month) : mr.month}</td>
                    {columns.map(c => (
                      <td key={c.key} className={`px-3 py-1.5 ${c.align || "text-right"}`}>
                        {c.render ? c.render(mr[c.key], mr) : mr[c.key]}
                      </td>
                    ))}
                  </tr>
                ))}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function fmtMonth(mk) {
  if (!mk || mk.length < 7) return mk;
  const y = mk.slice(0, 4), m = parseInt(mk.slice(5, 7), 10);
  const names = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  return `${names[m-1]} ${y}`;
}

function Bar({ pct }) {
  const w = Math.max(0, Math.min(100, pct || 0));
  return (
    <div className="w-24 h-1.5 bg-slate-100 rounded overflow-hidden inline-block align-middle">
      <div className="h-full bg-orange-500" style={{ width: `${w}%` }} />
    </div>
  );
}

export default function Reports() {
  const { user } = useAuth();
  const [filters, setFilters] = useState({
    unit_id: "", customer_id: "", start_date: "", end_date: "", status: "",
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [customers, setCustomers] = useState([]);
  const [units, setUnits] = useState([]);
  const [active, setActive] = useState("summary");   // selected report in the left nav
  const [expandedCust, setExpandedCust] = useState(new Set());  // machine report: expanded customer rows
  const toggleCust = (name) => setExpandedCust((prev) => {
    const next = new Set(prev);
    if (next.has(name)) next.delete(name); else next.add(name);
    return next;
  });
  const [expandedTon, setExpandedTon] = useState(new Set());  // machine report: expanded tonnage rows
  const toggleTon = (name) => setExpandedTon((prev) => {
    const next = new Set(prev);
    if (next.has(name)) next.delete(name); else next.add(name);
    return next;
  });

  async function load() {
    setLoading(true);
    try {
      const params = {};
      for (const k of Object.keys(filters)) {
        if (filters[k]) params[k] = filters[k];
      }
      const [s, c, u] = await Promise.all([
        api.get("/reports/summary", { params }),
        api.get("/customers"),
        user?.role === "admin" ? api.get("/units") : Promise.resolve({ data: [] }),
      ]);
      setData(s.data);
      setCustomers(c.data || []);
      setUnits(u.data || []);
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters]);

  const rfqQuarterly = useMemo(() => {
    return groupByQuarter(data?.rfqs_by_period || [], ["received", "won", "lost", "dropped", "in_flight"]);
  }, [data]);

  const winQuarterly = useMemo(() => {
    const grouped = groupByQuarter(data?.win_rate_trend || [], ["won", "lost", "contested"]);
    grouped.forEach(g => {
      g.win_rate_pct = g.contested ? Math.round((g.won / g.contested) * 1000) / 10 : null;
    });
    return grouped;
  }, [data]);

  const awardedQuarterly = useMemo(() => {
    return groupByQuarter(data?.awarded_by_period || [], ["npd_value", "asm_value", "total_value", "npd_count", "asm_count"]);
  }, [data]);

  async function downloadWorkbook() {
    const params = new URLSearchParams();
    for (const k of Object.keys(filters)) if (filters[k]) params.set(k, filters[k]);
    const url = `/reports/export.xlsx?${params.toString()}`;
    const res = await api.get(url, { responseType: "blob" });
    const blob = new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    const fname = (res.headers?.["content-disposition"] || "").match(/filename="([^"]+)"/)?.[1] || "AAMPL_Reports.xlsx";
    link.download = fname;
    link.click();
    URL.revokeObjectURL(link.href);
  }

  const h = data?.headline || {};
  const winPctDisplay = h.win_rate_pct == null ? "—" : `${h.win_rate_pct}%`;

  // Left-nav report catalogue — click one to show that report on the right.
  const SECTIONS = [
    { group: "Overview", items: [
      { id: "summary", label: "Summary" },
      { id: "totals", label: "Totals & Status" },
      { id: "segment", label: "2W vs 4W" },
    ] },
    { group: "RFQ Analytics", items: [
      { id: "rfqs",    label: "RFQs by Period" },
      { id: "winrate", label: "Win-rate Trend" },
      { id: "awarded", label: "Awarded by Period" },
    ] },
    { group: "Customers", items: [
      { id: "cust_awarded",  label: "Top by Awarded Business" },
      { id: "cust_activity", label: "Top by RFQ Activity" },
    ] },
    { group: "Capacity & Demand", items: [
      { id: "rm_demand", label: "Raw Material Demand" },
      { id: "machines",  label: "Machine Requirement" },
    ] },
  ];

  function renderSection() {
    switch (active) {
      case "rfqs":
        return (
          <section data-testid="reports-rfqs-section">
            <h2 className="text-base font-semibold text-slate-900 mb-2">RFQs received by period</h2>
            <QuarterTable
              rows={rfqQuarterly}
              monthFmt={fmtMonth}
              columns={[
                { key: "received",  label: "Received" },
                { key: "won",       label: "Won" },
                { key: "lost",      label: "Lost" },
                { key: "dropped",   label: "Dropped" },
                { key: "in_flight", label: "In-flight" },
              ]}
            />
          </section>
        );
      case "winrate":
        return (
          <section data-testid="reports-winrate-section">
            <h2 className="text-base font-semibold text-slate-900 mb-2">Win-rate trend</h2>
            <QuarterTable
              rows={winQuarterly}
              monthFmt={fmtMonth}
              columns={[
                { key: "won",          label: "Won" },
                { key: "lost",         label: "Lost" },
                { key: "contested",    label: "Contested" },
                { key: "win_rate_pct", label: "Win %",
                  render: (v) => (
                    <span className="inline-flex items-center gap-2 justify-end">
                      <Bar pct={v} />
                      <span className="w-12 text-right">{v == null ? "—" : `${v}%`}</span>
                    </span>
                  ),
                },
              ]}
            />
          </section>
        );
      case "awarded":
        return (
          <section data-testid="reports-awarded-section">
            <h2 className="text-base font-semibold text-slate-900 mb-2">Awarded business by period</h2>
            <QuarterTable
              rows={awardedQuarterly}
              monthFmt={fmtMonth}
              columns={[
                { key: "npd_value",   label: "NPD value",   render: v => short(v) },
                { key: "asm_value",   label: "Asm value",   render: v => short(v) },
                { key: "total_value", label: "Total",       render: v => <span className="font-semibold text-emerald-700">{short(v)}</span> },
                { key: "npd_count",   label: "NPDs" },
                { key: "asm_count",   label: "Assemblies" },
              ]}
            />
          </section>
        );
      case "cust_awarded":
        return (
          <section data-testid="reports-top-customers-section">
            <h2 className="text-base font-semibold text-slate-900 mb-2">Top customers by awarded business</h2>
            <div className="overflow-x-auto border border-slate-200 rounded-lg">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-600">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium w-10">#</th>
                    <th className="px-3 py-2 text-left font-medium">Customer</th>
                    <th className="px-3 py-2 text-right font-medium">Awarded items</th>
                    <th className="px-3 py-2 text-right font-medium">NPDs</th>
                    <th className="px-3 py-2 text-right font-medium">Assemblies</th>
                    <th className="px-3 py-2 text-right font-medium">Awarded value</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.top_customers || []).length === 0 && (
                    <tr><td colSpan={6} className="px-3 py-4 text-center text-slate-400 italic">No awarded business in selected range.</td></tr>
                  )}
                  {(data.top_customers || []).map((c, i) => (
                    <tr key={c.name + i} className="border-t border-slate-100">
                      <td className="px-3 py-2 text-slate-500">{i + 1}</td>
                      <td className="px-3 py-2 font-medium text-slate-800">{c.name}</td>
                      <td className="px-3 py-2 text-right">{c.awarded_count}</td>
                      <td className="px-3 py-2 text-right">{c.npd_count}</td>
                      <td className="px-3 py-2 text-right">{c.asm_count}</td>
                      <td className="px-3 py-2 text-right font-semibold text-emerald-700">{short(c.awarded_value)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        );
      case "cust_activity":
        return (
          <section data-testid="reports-customer-activity-section">
            <h2 className="text-base font-semibold text-slate-900 mb-1">Top customers by RFQ activity</h2>
            <p className="text-xs text-slate-500 mb-2">
              Independent of win/loss. Rank by number of RFQs received, or by total quoted (pipeline) value of their parts.
            </p>
            <CustomerActivityTable rows={data.top_customers_activity} />
          </section>
        );
      case "rm_demand":
        return (
          <section data-testid="reports-rm-demand-section">
            <h2 className="text-base font-semibold text-slate-900 mb-1">Raw material demand</h2>
            <p className="text-xs text-slate-500 mb-3">
              Across all costed RFQ parts. Rank by number of parts asking for it, or by RM weight
              (peak annual pcs × per-piece gross weight) — the weight view shows which RM is most
              strategically important. Thickness covers sheet/strip parts only.
            </p>
            <div className="grid lg:grid-cols-2 gap-4">
              <RmFreqTable title="By RM grade" labelHead="RM Grade" labelKey="grade" rows={data.rm_grade_frequency} />
              <RmFreqTable title="By thickness (sheet / strip)" labelHead="Thickness" labelKey="thickness_mm" rows={data.rm_thickness_frequency} isThickness />
            </div>
          </section>
        );
      case "machines":
        return (
          <section data-testid="reports-machine-section">
            <h2 className="text-base font-semibold text-slate-900 mb-1">
              Machine requirement <span className="text-sm font-normal text-slate-500">· Total {Number(h.total_machines ?? 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })} machine-load · {Number(h.total_machines_needed ?? 0)} machines to buy</span>
            </h2>
            <p className="text-xs text-slate-500 mb-3">
              From process cycle time × peak monthly volume, split by machine / tonnage. Capacity: Press 26d×10h = 260 h/mo · CNC/VMC 26d×20h = 520 h/mo. "Machines needed" rounds each tonnage's load UP to whole machines.
            </p>

            {/* By machine / tonnage — the real capacity buy-list */}
            <div className="bg-white border border-slate-200 rounded-lg overflow-hidden mb-4">
              <div className="px-3 py-2 text-xs font-semibold text-slate-600 bg-slate-50 border-b border-slate-100">By machine / tonnage <span className="font-normal text-slate-400">· click a row for customer split</span></div>
              <table className="w-full text-sm">
                <thead className="text-[10px] uppercase tracking-wider text-slate-400">
                  <tr>
                    <th className="text-left px-3 py-2">Machine / Tonnage</th>
                    <th className="text-right px-3 py-2">Capacity (h/mo)</th>
                    <th className="text-right px-3 py-2">Hrs/mo</th>
                    <th className="text-right px-3 py-2" title="Fractional load">Load</th>
                    <th className="text-right px-3 py-2" title="Load rounded up to whole machines">Machines needed</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {(data.machines_by_tonnage || []).length === 0 && <tr><td colSpan={5} className="px-3 py-4 text-center text-xs text-slate-400">No machine data (add process cycle times + tonnage + volumes).</td></tr>}
                  {(data.machines_by_tonnage || []).map((r) => {
                    const isOpen = expandedTon.has(r.tonnage);
                    const custs = r.by_customer || [];
                    const label = r.tonnage === "(unassigned)" ? "Unassigned" : r.tonnage;
                    return (
                    <Fragment key={r.tonnage}>
                    <tr className="cursor-pointer hover:bg-slate-50" onClick={() => toggleTon(r.tonnage)}>
                      <td className="px-3 py-2 text-slate-700 font-medium">
                        <span className="inline-block w-3 text-slate-400">{custs.length ? (isOpen ? "▾" : "▸") : ""}</span> {label}
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-slate-400">{r.capacity}</td>
                      <td className="px-3 py-2 text-right font-mono text-slate-600">{r.hours.toLocaleString("en-IN", { maximumFractionDigits: 1 })}</td>
                      <td className="px-3 py-2 text-right font-mono text-slate-500">{Number(r.load).toLocaleString("en-IN", { maximumFractionDigits: 2 })}</td>
                      <td className="px-3 py-2 text-right font-mono font-bold text-indigo-700">{r.machines_needed}</td>
                    </tr>
                    {isOpen && custs.map((c) => (
                      <tr key={r.tonnage + "|" + c.name} className="bg-slate-50/60">
                        <td className="pl-9 pr-3 py-1.5 text-xs text-slate-500">{c.name}</td>
                        <td className="px-3 py-1.5"></td>
                        <td className="px-3 py-1.5 text-right font-mono text-xs text-slate-500">{c.hours.toLocaleString("en-IN", { maximumFractionDigits: 1 })}</td>
                        <td className="px-3 py-1.5 text-right font-mono text-xs text-slate-600">{Number(c.load).toLocaleString("en-IN", { maximumFractionDigits: 2 })}</td>
                        <td className="px-3 py-1.5"></td>
                      </tr>
                    ))}
                    </Fragment>
                  );})}
                </tbody>
              </table>
            </div>

            <div className="grid lg:grid-cols-2 gap-4">
              <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
                <div className="px-3 py-2 text-xs font-semibold text-slate-600 bg-slate-50 border-b border-slate-100">By customer <span className="font-normal text-slate-400">· click a row for tonnage split</span></div>
                <table className="w-full text-sm">
                  <thead className="text-[10px] uppercase tracking-wider text-slate-400">
                    <tr><th className="text-left px-3 py-2">Customer</th><th className="text-right px-3 py-2">Parts</th><th className="text-right px-3 py-2">Hrs/mo</th><th className="text-right px-3 py-2">Machines (load)</th></tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {(data.machines_by_customer || []).length === 0 && <tr><td colSpan={4} className="px-3 py-4 text-center text-xs text-slate-400">No machine data (add process cycle times + volumes).</td></tr>}
                    {(data.machines_by_customer || []).map((r) => {
                      const isOpen = expandedCust.has(r.name);
                      const tons = r.by_tonnage || [];
                      return (
                      <Fragment key={r.name}>
                      <tr className="cursor-pointer hover:bg-slate-50" onClick={() => toggleCust(r.name)}>
                        <td className="px-3 py-2 text-slate-700">
                          <span className="inline-block w-3 text-slate-400">{tons.length ? (isOpen ? "▾" : "▸") : ""}</span> {r.name}
                        </td>
                        <td className="px-3 py-2 text-right text-slate-500">{r.parts}</td>
                        <td className="px-3 py-2 text-right font-mono text-slate-600">{r.hours.toLocaleString("en-IN", { maximumFractionDigits: 1 })}</td>
                        <td className="px-3 py-2 text-right font-mono font-bold text-indigo-700">{Number(r.machines).toLocaleString("en-IN", { maximumFractionDigits: 2 })}</td>
                      </tr>
                      {isOpen && tons.map((t) => (
                        <tr key={r.name + t.tonnage} className="bg-slate-50/60">
                          <td className="pl-9 pr-3 py-1.5 text-xs text-slate-500">{t.tonnage === "(unassigned)" ? "Unassigned" : t.tonnage}</td>
                          <td className="px-3 py-1.5"></td>
                          <td className="px-3 py-1.5 text-right font-mono text-xs text-slate-500">{t.hours.toLocaleString("en-IN", { maximumFractionDigits: 1 })}</td>
                          <td className="px-3 py-1.5 text-right font-mono text-xs text-slate-600">{Number(t.load).toLocaleString("en-IN", { maximumFractionDigits: 2 })} <span className="text-slate-400">({t.machines_needed} m/c)</span></td>
                        </tr>
                      ))}
                      {isOpen && tons.length === 0 && (
                        <tr className="bg-slate-50/60"><td colSpan={4} className="pl-9 pr-3 py-1.5 text-xs text-slate-400 italic">No tonnage assigned.</td></tr>
                      )}
                      </Fragment>
                    );})}
                  </tbody>
                </table>
              </div>
              <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
                <div className="px-3 py-2 text-xs font-semibold text-slate-600 bg-slate-50 border-b border-slate-100">By part</div>
                <table className="w-full text-sm">
                  <thead className="text-[10px] uppercase tracking-wider text-slate-400">
                    <tr><th className="text-left px-3 py-2">Part</th><th className="text-left px-3 py-2">Customer</th><th className="text-right px-3 py-2">Hrs/mo</th><th className="text-right px-3 py-2" title="Fractional load — parts share machines">Load</th></tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {(data.machines_by_part || []).length === 0 && <tr><td colSpan={4} className="px-3 py-4 text-center text-xs text-slate-400">No machine data.</td></tr>}
                    {(data.machines_by_part || []).map((r, i) => (
                      <tr key={i}>
                        <td className="px-3 py-2 text-slate-700">{r.part_name}<span className="text-slate-400 text-xs ml-1">{r.part_number !== "—" ? r.part_number : ""}</span></td>
                        <td className="px-3 py-2 text-slate-500 text-xs">{r.customer}</td>
                        <td className="px-3 py-2 text-right font-mono text-slate-600">{r.hours.toLocaleString("en-IN", { maximumFractionDigits: 1 })}</td>
                        <td className="px-3 py-2 text-right font-mono font-bold text-indigo-700">{r.machines.toLocaleString("en-IN", { maximumFractionDigits: 2 })}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </section>
        );
      case "segment": {
        const cs = (data.category_split || {}).costings || { "2W": 0, "4W": 0, Unset: 0, total: 0 };
        const pj = (data.category_split || {}).projects || { "2W": 0, "4W": 0, Unset: 0, total: 0 };
        const ap = (data.category_split || {}).apqp || { "2W": 0, "4W": 0, Unset: 0, total: 0 };
        const SegCard = ({ title, d }) => {
          const two = d["2W"] || 0, four = d["4W"] || 0, unset = d.Unset || 0, total = d.total || 0;
          const pct = (x) => (total ? Math.round((x / total) * 100) : 0);
          return (
            <div className="bg-white border border-slate-200 rounded-lg p-4">
              <div className="flex items-baseline justify-between mb-3">
                <h3 className="text-sm font-semibold text-slate-700">{title}</h3>
                <span className="text-xs text-slate-400">Total {total}</span>
              </div>
              <div className="flex h-3 rounded-full overflow-hidden bg-slate-100 mb-3">
                <div className="bg-sky-500" style={{ width: `${pct(two)}%` }} title={`2W ${two}`} />
                <div className="bg-indigo-500" style={{ width: `${pct(four)}%` }} title={`4W ${four}`} />
                <div className="bg-slate-300" style={{ width: `${pct(unset)}%` }} title={`Unset ${unset}`} />
              </div>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="rounded-md bg-sky-50 py-2">
                  <div className="text-lg font-bold text-sky-700">{two}</div>
                  <div className="text-[11px] text-slate-500">2W · {pct(two)}%</div>
                </div>
                <div className="rounded-md bg-indigo-50 py-2">
                  <div className="text-lg font-bold text-indigo-700">{four}</div>
                  <div className="text-[11px] text-slate-500">4W · {pct(four)}%</div>
                </div>
                <div className="rounded-md bg-slate-50 py-2">
                  <div className="text-lg font-bold text-slate-500">{unset}</div>
                  <div className="text-[11px] text-slate-400">Unset · {pct(unset)}%</div>
                </div>
              </div>
            </div>
          );
        };
        return (
          <div data-testid="reports-segment-section">
            <h2 className="text-base font-semibold text-slate-900 mb-1">2W vs 4W split</h2>
            <p className="text-xs text-slate-500 mb-3">How many costings, projects and APQP projects are 2-Wheeler vs 4-Wheeler in the selected range.</p>
            <div className="grid md:grid-cols-3 gap-4">
              <SegCard title="Costings" d={cs} />
              <SegCard title="Projects" d={pj} />
              <SegCard title="APQP Projects" d={ap} />
            </div>
          </div>
        );
      }
      case "summary":
      default:
        return (
          <div data-testid="reports-summary-section">
            <h2 className="text-base font-semibold text-slate-900 mb-3">Summary</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <Kpi icon={Inbox} label="RFQs received" value={h.total_rfqs ?? "—"} accent="sky" />
              <Kpi icon={Award} label="Won / Lost" value={`${h.won ?? 0} / ${h.lost ?? 0}`} accent="emerald" />
              <Kpi icon={TrendingUp} label="Win rate" value={winPctDisplay} accent="orange" />
              <Kpi icon={FileText} label="Costings (single)" value={h.total_costings ?? "—"} accent="indigo" />
              <Kpi icon={Layers} label="Assemblies" value={h.total_assemblies ?? "—"} accent="sky" />
              <Kpi icon={FolderKanban} label="Projects" value={h.total_projects ?? "—"} accent="slate" />
              <Kpi icon={BarChart3} label="RFQ value" value={short(h.total_rfq_value)} accent="indigo" />
              <Kpi icon={Award} label="Awarded value" value={short(h.total_awarded_value)} accent="amber" />
              <Kpi icon={Users} label="Top customer" value={h.top_customer || "—"} accent="slate" small />
            </div>
          </div>
        );

      case "totals": {
        const t = data?.totals || {};
        const oc = [
          { k: "won", label: "Won", cls: "text-emerald-700" },
          { k: "lost", label: "Lost", cls: "text-red-700" },
          { k: "dropped", label: "Dropped", cls: "text-stone-600" },
          { k: "partially_awarded", label: "Partial", cls: "text-purple-700" },
          { k: "pending", label: "Pending", cls: "text-slate-600" },
        ];
        const st = [
          { k: "approved", label: "Approved", cls: "text-emerald-700" },
          { k: "under_approval", label: "Under Approval", cls: "text-amber-700" },
          { k: "draft", label: "Draft", cls: "text-slate-600" },
        ];
        const Block = ({ title, total, outcome, status }) => (
          <div className="bg-white border border-slate-200 rounded-lg p-4">
            <div className="flex items-baseline justify-between mb-3">
              <div className="text-sm font-semibold text-slate-800">{title}</div>
              <div className="text-2xl font-bold text-slate-900">{total ?? 0}</div>
            </div>
            <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1">By outcome</div>
            <div className="grid grid-cols-5 gap-1 mb-3">
              {oc.map(o => (
                <div key={o.k} className="text-center rounded bg-slate-50 py-1.5">
                  <div className={`text-base font-bold ${o.cls}`}>{(outcome || {})[o.k] ?? 0}</div>
                  <div className="text-[9px] text-slate-500">{o.label}</div>
                </div>
              ))}
            </div>
            <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1">By status</div>
            <div className="grid grid-cols-3 gap-1">
              {st.map(s => (
                <div key={s.k} className="text-center rounded bg-slate-50 py-1.5">
                  <div className={`text-base font-bold ${s.cls}`}>{(status || {})[s.k] ?? 0}</div>
                  <div className="text-[9px] text-slate-500">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        );
        return (
          <div data-testid="reports-totals-section">
            <h2 className="text-base font-semibold text-slate-900 mb-1">Totals &amp; Status</h2>
            <p className="text-xs text-slate-500 mb-3">Counts for the current filters (unit / customer / date range). Won, Lost, Draft, Approved etc.</p>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
              <Block title="Costings (single)" total={t.costings} outcome={t.costings_by_outcome} status={t.costings_by_status} />
              <Block title="Assemblies" total={t.assemblies} outcome={t.assemblies_by_outcome} status={t.assemblies_by_status} />
              <Block title="Projects" total={t.projects} outcome={t.projects_by_outcome} status={t.projects_by_status} />
            </div>
          </div>
        );
      }
    }
  }

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="reports-page">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <BarChart3 size={22} className="text-orange-600" /> Reports
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">Quarterly headline with monthly drill-down. Click a quarter row to expand.</p>
        </div>
        <button
          onClick={downloadWorkbook}
          className="inline-flex items-center gap-1.5 px-3 py-2 text-sm bg-orange-500 text-white rounded-md hover:bg-orange-600"
          data-testid="reports-export-workbook"
        >
          <Download size={14} /> Export Workbook
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-lg p-3 mb-4">
        <div className="flex items-center gap-2 text-xs font-semibold text-slate-500 mb-2 uppercase tracking-wider"><Filter size={12} /> Filters</div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
          {user?.role === "admin" && (
            <SearchableSelect
              value={filters.unit_id}
              onChange={(v) => setFilters(f => ({ ...f, unit_id: v }))}
              options={[{ value: "", label: "Unit: All" }, ...units.map(u => ({ value: u.unit_id, label: `${u.unit_code} — ${u.unit_name}` }))]}
              placeholder="Unit: type to search…"
              className="w-full text-sm border border-slate-200 rounded-md px-2 py-1.5"
              testid="reports-filter-unit"
            />
          )}
          <SearchableSelect
            value={filters.customer_id}
            onChange={(v) => setFilters(f => ({ ...f, customer_id: v }))}
            options={[{ value: "", label: "Customer: All" }, ...customers.map(c => ({ value: c.customer_id, label: c.name }))]}
            placeholder="Customer: type to search…"
            className="w-full text-sm border border-slate-200 rounded-md px-2 py-1.5"
            testid="reports-filter-customer"
          />
          <input type="date" value={filters.start_date} onChange={e => setFilters(f => ({ ...f, start_date: e.target.value }))} className="text-sm border border-slate-200 rounded-md px-2 py-1.5" data-testid="reports-filter-start" />
          <input type="date" value={filters.end_date} onChange={e => setFilters(f => ({ ...f, end_date: e.target.value }))} className="text-sm border border-slate-200 rounded-md px-2 py-1.5" data-testid="reports-filter-end" />
          <SearchableSelect
            value={filters.status}
            onChange={(v) => setFilters(f => ({ ...f, status: v }))}
            options={STATUS_OPTIONS.map(s => ({ value: s.v, label: `RFQ status: ${s.label}` }))}
            placeholder="RFQ status: type to search…"
            className="w-full text-sm border border-slate-200 rounded-md px-2 py-1.5"
            testid="reports-filter-status"
          />
        </div>
      </div>

      {loading && <div className="text-sm text-slate-400">Loading…</div>}

      {!loading && data && (
        <div className="flex flex-col lg:flex-row gap-4 items-start">
          {/* Left: report catalogue */}
          <nav className="w-full lg:w-56 shrink-0 bg-white border border-slate-200 rounded-lg p-2" data-testid="reports-nav">
            {SECTIONS.map(g => (
              <div key={g.group} className="mb-2">
                <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 px-2 mb-1">{g.group}</div>
                {g.items.map(it => (
                  <button
                    key={it.id}
                    onClick={() => setActive(it.id)}
                    className={`w-full text-left px-3 py-1.5 rounded-md text-sm mb-0.5 border-l-2 transition ${active === it.id ? "bg-orange-50 text-orange-700 border-orange-500 font-medium" : "border-transparent text-slate-600 hover:bg-slate-50"}`}
                    data-testid={`reports-nav-${it.id}`}
                  >
                    {it.label}
                  </button>
                ))}
              </div>
            ))}
          </nav>
          {/* Right: selected report */}
          <div className="flex-1 min-w-0 w-full">
            {renderSection()}
          </div>
        </div>
      )}
    </div>
  );
}

function CustomerActivityTable({ rows = [] }) {
  const [sortBy, setSortBy] = useState("rfq_count");
  const sorted = useMemo(
    () => [...(rows || [])].sort((a, b) => (Number(b[sortBy]) || 0) - (Number(a[sortBy]) || 0)),
    [rows, sortBy]
  );
  const btn = (key, label) => (
    <button
      type="button"
      onClick={() => setSortBy(key)}
      className={`px-2 py-1 ${sortBy === key ? "bg-orange-600 text-white" : "text-slate-600 hover:bg-slate-50"}`}
      data-testid={`cust-sort-${key}`}
    >
      {label}
    </button>
  );
  return (
    <div>
      <div className="flex justify-end mb-2">
        <div className="inline-flex text-[11px] border border-slate-200 rounded overflow-hidden">
          {btn("rfq_count", "By RFQs")}
          {btn("rfq_value", "By value")}
        </div>
      </div>
      <div className="overflow-x-auto border border-slate-200 rounded-lg">
        <table className="w-full text-sm min-w-[480px]">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="px-3 py-2 text-left font-medium w-10">#</th>
              <th className="px-3 py-2 text-left font-medium">Customer</th>
              <th className="px-3 py-2 text-right font-medium">RFQs</th>
              <th className="px-3 py-2 text-right font-medium">Number of parts</th>
              <th className="px-3 py-2 text-right font-medium">RFQ value</th>
            </tr>
          </thead>
          <tbody>
            {sorted.length === 0 && (
              <tr><td colSpan={5} className="px-3 py-4 text-center text-slate-400 italic">No RFQs in selected range.</td></tr>
            )}
            {sorted.map((c, i) => (
              <tr key={c.name + i} className="border-t border-slate-100">
                <td className="px-3 py-2 text-slate-500">{i + 1}</td>
                <td className="px-3 py-2 font-medium text-slate-800">{c.name}</td>
                <td className="px-3 py-2 text-right">{c.rfq_count}</td>
                <td className="px-3 py-2 text-right text-slate-600">{c.npd_count}</td>
                <td className="px-3 py-2 text-right font-semibold text-emerald-700">{short(c.rfq_value)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function RmFreqTable({ title, labelHead, labelKey, rows = [], isThickness = false }) {
  const [sortBy, setSortBy] = useState("rm_weight_kg");
  const sorted = useMemo(
    () => [...(rows || [])].sort((a, b) => (Number(b[sortBy]) || 0) - (Number(a[sortBy]) || 0)),
    [rows, sortBy]
  );
  const fmtPcs = (v) => (Number(v) || 0).toLocaleString("en-IN");
  const fmtKg = (v) => `${(Number(v) || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })} kg`;
  const btn = (key, label) => (
    <button
      type="button"
      onClick={() => setSortBy(key)}
      className={`px-2 py-1 ${sortBy === key ? "bg-orange-600 text-white" : "text-slate-600 hover:bg-slate-50"}`}
      data-testid={`rm-sort-${key}`}
    >
      {label}
    </button>
  );
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-semibold text-slate-800">{title}</h3>
        <div className="inline-flex text-[11px] border border-slate-200 rounded overflow-hidden">
          {btn("part_count", "By parts")}
          {btn("rm_weight_kg", "By RM weight")}
        </div>
      </div>
      <div className="overflow-x-auto border border-slate-200 rounded-lg">
        <table className="w-full text-sm min-w-[440px]">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="px-3 py-2 text-left font-medium w-10">#</th>
              <th className="px-3 py-2 text-left font-medium">{labelHead}</th>
              <th className="px-3 py-2 text-right font-medium">Parts</th>
              <th className="px-3 py-2 text-right font-medium">Annual pcs</th>
              <th className="px-3 py-2 text-right font-medium">RM wt / yr</th>
            </tr>
          </thead>
          <tbody>
            {sorted.length === 0 && (
              <tr><td colSpan={5} className="px-3 py-4 text-center text-slate-400 italic">No data in selected range.</td></tr>
            )}
            {sorted.map((r, i) => (
              <tr key={(r[labelKey] ?? "") + "-" + i} className="border-t border-slate-100">
                <td className="px-3 py-2 text-slate-500">{i + 1}</td>
                <td className="px-3 py-2 font-medium text-slate-800">{isThickness ? `${r[labelKey]} mm` : r[labelKey]}</td>
                <td className="px-3 py-2 text-right">{r.part_count}</td>
                <td className="px-3 py-2 text-right text-slate-600">{fmtPcs(r.annual_pcs)}</td>
                <td className="px-3 py-2 text-right font-semibold text-emerald-700">{fmtKg(r.rm_weight_kg)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Kpi({ icon: Icon, label, value, accent = "slate", small = false }) {
  const colorMap = {
    sky: "bg-sky-50 text-sky-700",
    emerald: "bg-emerald-50 text-emerald-700",
    orange: "bg-orange-50 text-orange-700",
    amber: "bg-amber-50 text-amber-700",
    indigo: "bg-indigo-50 text-indigo-700",
    slate: "bg-slate-50 text-slate-700",
  };
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-3 flex items-start gap-3">
      <div className={`w-9 h-9 rounded-md flex items-center justify-center ${colorMap[accent]}`}>
        <Icon size={16} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{label}</div>
        <div
          className={`${small ? "text-sm" : "text-base"} font-bold text-slate-900 leading-tight break-words`}
          title={value == null ? undefined : String(value)}
        >
          {value}
        </div>
      </div>
    </div>
  );
}
